import { useCallback, useEffect, useMemo, useRef } from "react";
import { GeoJSON, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import type { Marker as LeafletMarker } from "leaflet";
import type { GeoJsonObject } from "geojson";
import FloraIQMap from "@/components/shared/FloraIQMap";

export interface Coordinates {
  latitude: number;
  longitude: number;
}

interface TreeMapPickerProps {
  value: Coordinates | null;
  onChange: (coords: Coordinates) => void;
  /** Trees already registered nearby, drawn so the operator sees what is there. */
  nearby?: Array<{ id: number; latitude: number | null; longitude: number | null; cta_code: string }>;
  /** Pilot area outline, so it is visible where cadastral data actually exists. */
  studyArea?: GeoJsonObject | null;
  /** Block outlines of that area — what the point will be matched against. */
  blocks?: GeoJsonObject | null;
  height?: number;
}

// Leaflet's default icon resolves its images relative to the CSS, which breaks
// under Vite's bundling. Drawing the marker as a div avoids the broken-image
// icon without shipping the asset files.
const trunkIcon = L.divIcon({
  className: "",
  html: `
    <div style="position:relative;width:28px;height:36px;">
      <div style="position:absolute;left:50%;top:0;transform:translateX(-50%);
                  width:26px;height:26px;border-radius:50%;
                  background:#1B6E4F;border:3px solid #fff;
                  box-shadow:0 2px 6px rgba(0,0,0,.4);"></div>
      <div style="position:absolute;left:50%;top:22px;transform:translateX(-50%);
                  width:0;height:0;border-left:6px solid transparent;
                  border-right:6px solid transparent;border-top:12px solid #1B6E4F;"></div>
    </div>`,
  iconSize: [28, 36],
  iconAnchor: [14, 34],
});

const nearbyIcon = L.divIcon({
  className: "",
  html: `<div style="width:14px;height:14px;border-radius:50%;
                     background:#E8A33D;border:2px solid #fff;
                     box-shadow:0 1px 4px rgba(0,0,0,.35);"></div>`,
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

function ClickCapture({ onChange }: { onChange: (c: Coordinates) => void }) {
  useMapEvents({
    click(event) {
      onChange({ latitude: event.latlng.lat, longitude: event.latlng.lng });
    },
  });
  return null;
}

/**
 * Brings the marker back into view when it lands outside the current extent.
 *
 * Typed coordinates can be anywhere, and without this the operator positions a
 * marker they cannot see. Only pans when the point is actually off-screen, so
 * dragging the marker — which keeps it inside the viewport — is left alone.
 */
function KeepMarkerInView({ value }: { value: Coordinates | null }) {
  const map = useMap();

  useEffect(() => {
    if (!value) return;
    const point = L.latLng(value.latitude, value.longitude);
    if (!map.getBounds().contains(point)) {
      map.setView(point, Math.max(map.getZoom(), 19));
    }
  }, [map, value]);

  return null;
}

/**
 * Fits the map to the study area the first time it arrives.
 *
 * Only when there is no coordinate yet: once the operator has placed a marker,
 * moving the view under them would be hostile.
 */
function FitStudyArea({
  area,
  hasCoordinate,
}: {
  area: GeoJsonObject | null | undefined;
  hasCoordinate: boolean;
}) {
  const map = useMap();
  const fitted = useRef(false);

  useEffect(() => {
    if (!area || fitted.current || hasCoordinate) return;
    const layer = L.geoJSON(area);
    const bounds = layer.getBounds();
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [20, 20] });
      fitted.current = true;
    }
  }, [map, area, hasCoordinate]);

  return null;
}

export default function TreeMapPicker({
  value,
  onChange,
  nearby = [],
  studyArea,
  blocks,
  height = 380,
}: TreeMapPickerProps) {
  const markerRef = useRef<LeafletMarker | null>(null);

  // The map is only centred on the first coordinate. Re-centring on every
  // change would fight the operator while they drag the marker.
  const initialCenter = useMemo<[number, number] | undefined>(
    () => (value ? [value.latitude, value.longitude] : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleDragEnd = useCallback(() => {
    const marker = markerRef.current;
    if (!marker) return;
    const position = marker.getLatLng();
    onChange({ latitude: position.lat, longitude: position.lng });
  }, [onChange]);

  useEffect(() => {
    const marker = markerRef.current;
    if (marker && value) {
      marker.setLatLng([value.latitude, value.longitude]);
    }
  }, [value]);

  return (
    <div className="relative rounded-xl overflow-hidden border border-background-200">
      <FloraIQMap
        center={initialCenter}
        zoom={value ? 19 : 15}
        maxZoom={22}
        defaultBaseLayer="hybrid"
        style={{ height, width: "100%" }}
      >
        <ClickCapture onChange={onChange} />
        <KeepMarkerInView value={value} />
        <FitStudyArea area={studyArea} hasCoordinate={value !== null} />

        {blocks && (
          <GeoJSON
            key="blocks"
            data={blocks}
            style={{ color: "#4A90D9", weight: 1, fillOpacity: 0.06, fillColor: "#4A90D9" }}
            interactive={false}
          />
        )}
        {studyArea && (
          <GeoJSON
            key="study-area"
            data={studyArea}
            style={{ color: "#1B6E4F", weight: 2, dashArray: "6 4", fill: false }}
            interactive={false}
          />
        )}

        {nearby
          .filter((t) => t.latitude != null && t.longitude != null)
          .map((t) => (
            <Marker
              key={t.id}
              position={[t.latitude as number, t.longitude as number]}
              icon={nearbyIcon}
              interactive={false}
            />
          ))}

        {value && (
          <Marker
            position={[value.latitude, value.longitude]}
            icon={trunkIcon}
            draggable
            eventHandlers={{ dragend: handleDragEnd }}
            ref={(instance) => {
              markerRef.current = instance;
            }}
          />
        )}
      </FloraIQMap>

      <div className="absolute bottom-0 left-0 right-0 bg-white/95 border-t border-background-200 px-3 py-2 text-xs text-foreground-700 z-[1000]">
        {value ? (
          <>
            Arraste o marcador até a posição do{" "}
            <strong className="text-primary-600">tronco</strong> do exemplar.
            {nearby.length > 0 && (
              <span className="ml-2 text-[#8A6A2F]">
                ● exemplares já cadastrados por perto
              </span>
            )}
            {blocks && (
              <span className="ml-2 text-[#3B6FA0]">▭ quadras com base cadastral</span>
            )}
          </>
        ) : (
          <>Clique no mapa sobre a posição do tronco do exemplar arbóreo.</>
        )}
      </div>
    </div>
  );
}
